"use strict";
/**
 * OperationQueue<T, R> — strict FIFO single-consumer queue.
 *
 * Design invariants (per Decision N):
 * - Items are processed strictly in enqueue order.
 * - Only one item is processed at a time (single-consumer).
 * - A handler error rejects the Promise returned by that item's enqueue()
 *   call, but does NOT stall the queue — processing continues with the
 *   next item immediately.
 * - Concurrent producers are safe: multiple callers may call enqueue()
 *   simultaneously; their items are appended in call order and processed
 *   one by one.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.OperationQueue = void 0;
class OperationQueue {
    handler;
    pending = [];
    /** Promise for the currently-executing item, or null when idle. */
    running = null;
    constructor(handler) {
        this.handler = handler;
    }
    /**
     * Enqueue an item for processing.
     * Returns a Promise that resolves (or rejects) when the handler finishes
     * processing THIS item — not when the entire queue drains.
     */
    enqueue(item) {
        return new Promise((resolve, reject) => {
            this.pending.push({ item, resolve, reject });
            this.scheduleNext();
        });
    }
    /** Number of items waiting (not counting the currently-running item). */
    size() {
        return this.pending.length;
    }
    /**
     * Resolves when every item currently in the queue (including any
     * currently-running item) has been processed (whether successfully or not).
     * Items enqueued AFTER drain() is called are not awaited.
     */
    drain() {
        if (this.running === null && this.pending.length === 0) {
            return Promise.resolve();
        }
        // Collect a snapshot of all pending items plus the running slot,
        // then wait until they all settle.
        const snapshot = [...this.pending];
        return new Promise((resolve) => {
            if (snapshot.length === 0) {
                // Only a running item remains — attach to it.
                void (this.running ?? Promise.resolve()).then(() => resolve(), () => resolve());
                return;
            }
            // Wait for the last snapshot item to finish.
            const last = snapshot[snapshot.length - 1];
            if (last === undefined) {
                resolve();
                return;
            }
            const originalResolve = last.resolve;
            const originalReject = last.reject;
            last.resolve = (value) => {
                originalResolve(value);
                resolve();
            };
            last.reject = (reason) => {
                originalReject(reason);
                resolve();
            };
        });
    }
    // ---------------------------------------------------------------------------
    // Internal
    // ---------------------------------------------------------------------------
    scheduleNext() {
        if (this.running !== null || this.pending.length === 0) {
            return;
        }
        const entry = this.pending.shift();
        if (entry === undefined) {
            return;
        }
        this.running = this.processEntry(entry).then(() => {
            this.running = null;
            this.scheduleNext();
        });
    }
    async processEntry(entry) {
        try {
            const result = await this.handler(entry.item);
            entry.resolve(result);
        }
        catch (err) {
            entry.reject(err);
            // Queue continues — error is surfaced on the caller's Promise only.
        }
    }
}
exports.OperationQueue = OperationQueue;
//# sourceMappingURL=operation-queue.js.map